// Dashboard API client for communicating with the backend services
import { getServiceApiUrl } from '@airclou/utils'
import type { PurgeStore } from '@airclou/shared'
import { HTTP_TIMEOUT_MS } from '../config'
import { endUnusableSession, handleIdentityMismatch, isSameSessionIdentity, recoverRefusedToken, reportIdentityMismatch } from '@airclou/auth-react'
import { authServiceFetch } from './authServiceFetch'

// Mirrors `TenantErasurePreview` in the backend
// (apps/dashboard/backend/product/utils/tenantErasurePreview.ts).
export type PreviewStore = PurgeStore;

export type StorePreview =
  | { ok: true; rows: number; tables: Record<string, number>; detail: Record<string, unknown> }
  | { ok: false; error: string };

export type AccountFate =
  | { userId: string; outcome: 'deleted' }
  | { userId: string; outcome: 'kept'; remainsIn?: Array<{ id: string; name: string }> };

export interface TenantErasureAccounts {
  /** Member accounts erased with the organization. */
  toDelete: number;
  /** Member accounts kept because they belong to other organizations. */
  toKeep: number;
  /** The caller's own account; null when it could not be determined. */
  you: AccountFate | null;
}

export interface TenantErasurePreview {
  tenantId: string;
  stores: Record<PreviewStore, StorePreview>;
  /** Null when the membership queries failed — say so rather than showing zeros. */
  accounts: TenantErasureAccounts | null;
}

export interface TeamMember {
  id: string;
  user_id: string;
  tenant_id: string;
  role: string;
  permissions: string[];
  status: 'active' | 'inactive' | 'suspended';
  joined_at: string;
  updated_at: string;
  /** True for the organization's owner — read from tenant_role server-side. */
  is_tenant_admin: boolean;
  /**
   * The org role from `tenant_users`. It used to be overwritten with 'admin'
   * for whoever created the organization, which is why the old union was so
   * narrow; it now reports what the row actually holds.
   */
  tenant_role: 'owner' | 'admin' | 'billing_admin' | 'viewer' | 'user';
  // User details from auth service
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  last_login?: string;
  // Placeholder metadata (optional)
  placeholder?: boolean;
  placeholder_reason?: string;
  // Workspace memberships
  workspaces?: Array<{
    workspace_id: string;
    workspace_name: string;
    workspace_slug: string;
    workspace_role: 'admin' | 'member';
  }>;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string; details?: unknown };
}

export interface AuditLogEntry {
  id: string;
  tenant_id: string;
  workspace_id: string;
  category: string;
  resource_type: string;
  resource_id: string | null;
  resource_name: string | null;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_email: string | null;
  ip_address: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/** An open request to join this organization (pending, or escalated to support after 7 days). */
export interface AccessRequest {
  id: string;
  email: string;
  status: 'pending' | 'escalated';
  createdAt: string;
  escalatedAt: string | null;
}

class DashboardApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // Environment-aware URL selection - prioritize explicit environment variable
    if (import.meta.env.VITE_API_BASE_URL) {
      this.baseUrl = import.meta.env.VITE_API_BASE_URL;
    } else {
      // Use centralized service URL utility
      this.baseUrl = getServiceApiUrl('dashboard');
    }

    // Initialize token from localStorage if available
    this.refreshToken();
    // Bootstrap the file-session cookie so native <img>/CSS url() loads of /files/*
    // authenticate after a reload (token is read from localStorage without setToken).
    if (this.token) {
      this.syncFileSession('POST', this.token);
    }
  }

  refreshToken() {
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('dashboard:authToken');
      this.token = storedToken;
    }
  }

  setToken(token: string) {
    this.token = token;
    if (typeof window !== 'undefined') {
      localStorage.setItem('dashboard:authToken', token);
    }
    this.syncFileSession('POST', token);
  }

  /**
   * Mint (POST) or clear (DELETE) the file-scoped session cookie so native
   * <img>/CSS url() loads of /files/* authenticate — they can't send a Bearer header.
   * Best-effort; failures never block auth. The cookie is httpOnly + Path=/files and is
   * read by the resolver (backend routes/fileResolver.ts). See
   * maintenance/security/private-file-static-serve-exposure-fleet.md.
   */
  private syncFileSession(method: 'POST' | 'DELETE', token: string | null): void {
    if (typeof window === 'undefined') return;
    if (method === 'POST' && !token) return;
    fetch(`${this.baseUrl}/files/session`, {
      method,
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  }

  getToken(): string | null {
    // Refresh token from localStorage in case it was updated elsewhere
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('dashboard:authToken');
      this.token = storedToken;
    }
    return this.token;
  }

  clearToken() {
    this.syncFileSession('DELETE', null);
    this.token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('dashboard:authToken');
    }
  }

  /**
   * Read the airclou_csrf cookie value for CSRF double-submit.
   */
  private getCsrfToken(): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|;\s*)airclou_csrf=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Build headers for cross-service requests to the auth backend.
   * Includes Authorization (if available) and x-csrf-token (if available).
   */
  private buildAuthServiceHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const csrf = this.getCsrfToken();
    if (csrf) headers['x-csrf-token'] = csrf;
    return headers;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Decide what a 401 means. Resolves when the request should be sent once more
   * with the current token; otherwise throws.
   *
   * The token is a cache of the session cookie and can go stale while the
   * session is live — a laptop that slept through a renewal. Clearing it on the
   * first 401, as this used to, signed out a person who was signed in. So ask
   * the auth provider to renew from the cookie first, and clear only when that
   * cannot help.
   *
   * A renewal can adopt a different account: the cookie is shared across the
   * suite and may by now name someone who signed in elsewhere. So a write is
   * replayed only when the new token names the same user in the same
   * organization as the token the request was sent with — otherwise the
   * caller's payload would land on another account (as `authServiceFetch`
   * refuses for the same reason). Reads are safe to replay either way.
   */
  private async handleUnauthorized(
    errorData: any,
    sentToken: string | null,
    options: RequestInit,
    isRetry: boolean,
  ): Promise<void> {
    const code = errorData?.error?.code || errorData?.code;
    const authError = new Error('Authentication required');
    (authError as any).statusCode = 401;
    (authError as any).code = code;
    (authError as any).response = errorData;

    // A refusal of the replay is final: no second recovery, no loop. Unless the
    // provider is re-deriving (IDENTITY_MISMATCH), the session is unusable here:
    // hand it to the provider to sign out with a reason. Clearing the token
    // behind its back left a tab that looked signed in while every call failed.
    if (isRetry) {
      if (!handleIdentityMismatch(401, errorData) && !endUnusableSession()) this.clearToken();
      throw authError;
    }

    const method = (options.method ?? 'GET').toUpperCase();
    const isRead = method === 'GET' || method === 'HEAD';
    // The caller's deadline covers recovery too. If recovery spent it, a replay
    // can only abort, turning a renewed session into a timeout error.
    const canReplay = () => !(options.signal as AbortSignal | undefined)?.aborted;
    // The session is fine in both cases below, so neither carries a 401 status:
    // anything that reads a 401 as "signed out" would end a live session.
    const accountChanged = () => {
      const changed = new Error('You are now signed in as a different account. Reload the page and try again.');
      (changed as any).code = 'SESSION_ACCOUNT_CHANGED';
      return changed;
    };
    const timedOut = () => {
      const late = new Error('The request timed out while your session was renewed. Try again.');
      (late as any).code = 'REQUEST_TIMEOUT';
      return late;
    };

    // IDENTITY_MISMATCH means the cookie and this token name different users —
    // the cookie is the session, this token is the stale copy. Wait for the
    // provider to re-derive, then replay once. The new token names the cookie's
    // user, so only reads. This backend does not emit the code today; the check
    // is here so a proxied auth error cannot silently become a sign-out.
    if (await reportIdentityMismatch(401, errorData)) {
      if (!isRead) throw accountChanged();
      if (!canReplay()) throw timedOut();
      return;
    }

    const outcome = await recoverRefusedToken(sentToken);
    if (outcome === 'renewed') {
      if (!canReplay()) throw timedOut();
      if (isRead || isSameSessionIdentity(sentToken, this.getToken())) return;
      // Sent with no token, so there is no account to compare against — and no
      // reason to claim the account changed. Fail it plainly, without clearing.
      if (!sentToken) throw authError;
      throw accountChanged();
    }
    if (outcome === 'unreachable') {
      // No answer about the session: keep the token and fail this request as a
      // transient error, which callers retry or show as an error state.
      const unreachable = new Error('Could not renew your session right now');
      (unreachable as any).code = 'AUTH_UNREACHABLE';
      throw unreachable;
    }
    // The provider is signing out, with the reason. Nothing to clear here.
    if (outcome === 'ended') throw authError;
    // `refused` (nothing renewal can fix), or no provider mounted: the 401 stands.
    this.clearToken();
    throw authError;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Ensure we have the latest token
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: options.signal ?? AbortSignal.timeout(HTTP_TIMEOUT_MS),
      });

      if (!response.ok) {
        // Try to get error details from response body
        let errorData: any = null;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If we can't parse JSON, fall back to text
          try {
            const errorText = await response.text();
            errorData = { error: errorText };
          } catch (textError) {
            errorData = { error: `HTTP error! status: ${response.status}` };
          }
        }

        if (response.status === 401) {
          await this.handleUnauthorized(errorData, token, options, isRetry);
          return await this.request<T>(endpoint, options, true);
        }
        
        // Create error with proper structure for error code detection
        const apiError = new Error(errorData?.error?.message || errorData?.error || `HTTP error! status: ${response.status}`);
        (apiError as any).status = response.status;
        (apiError as any).code = errorData?.error?.code || errorData?.code;
        (apiError as any).response = errorData;
        throw apiError;
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return undefined as T;
      }

      const json = await response.json();
      if (json.success === false) {
        const apiError = new Error(json.error?.message || 'Request failed');
        (apiError as any).code = json.error?.code;
        (apiError as any).response = json;
        throw apiError;
      }
      return json.data;
    } catch (error) {
      // A replay's failure surfaces through the first attempt's catch; log once.
      if (!isRetry) console.error('API request failed:', error);
      throw error;
    }
  }

  private async requestPaginated<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<{ data: T[]; meta: { page: number; limit: number; total: number; totalPages: number } }> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, { ...options, headers, signal: options.signal ?? AbortSignal.timeout(HTTP_TIMEOUT_MS) });

      if (!response.ok) {
        let errorData: any = null;
        try {
          errorData = await response.json();
        } catch (parseError) {
          try {
            const errorText = await response.text();
            errorData = { error: errorText };
          } catch (textError) {
            errorData = { error: `HTTP error! status: ${response.status}` };
          }
        }

        if (response.status === 401) {
          await this.handleUnauthorized(errorData, token, options, isRetry);
          return await this.requestPaginated<T>(endpoint, options, true);
        }

        const apiError = new Error(errorData?.error?.message || errorData?.error || `HTTP error! status: ${response.status}`);
        (apiError as any).status = response.status;
        (apiError as any).code = errorData?.error?.code || errorData?.code;
        (apiError as any).response = errorData;
        throw apiError;
      }

      const json = await response.json();
      const { success, ...rest } = json;
      return rest;
    } catch (error) {
      // A replay's failure surfaces through the first attempt's catch; log once.
      if (!isRetry) console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication - calls auth service directly
  async getCurrentUser() {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/me`, { headers, signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  async getUserDetails(userIds: string[]) {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/users`, {
      method: 'POST',
      headers,
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      body: JSON.stringify({ userIds })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  private buildTeamMembersEndpoint(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    app_id?: string;
    sort?: string;
    order?: string;
  }): string {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.role && params.role !== 'all') searchParams.append('role', params.role);
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.app_id) searchParams.append('app_id', params.app_id);
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);

    const queryString = searchParams.toString();
    return `/team/members${queryString ? `?${queryString}` : ''}`;
  }

  // Team Members - Get team members for the current tenant
  async getTeamMembers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    app_id?: string;
    sort?: string;
    order?: string;
  }) {
    const endpoint = this.buildTeamMembersEndpoint(params);
    return this.requestPaginated<TeamMember>(endpoint);
  }

  // Get individual team member details
  async getTeamMember(memberId: string) {
    return this.request<TeamMember>(`/team/members/${memberId}`);
  }

  /**
   * Hand the organization to another member. The caller becomes an `admin`.
   *
   * Rejects with an Error carrying `.code` — `MEMBER_NOT_FOUND`,
   * `MEMBER_NOT_ACTIVE`, `OWNERSHIP_CHANGED`, `ALREADY_OWNER`. Branch on the
   * code, never the message.
   */
  async transferOwnership(tenantId: string, newOwnerUserId: string) {
    return this.request<{ tenantId: string; previousOwnerUserId: string; newOwnerUserId: string }>(
      `/tenants/${tenantId}/transfer-ownership`,
      { method: 'POST', body: JSON.stringify({ newOwnerUserId }) },
    );
  }

  /**
   * Shut the organization down: nobody can sign in to it and the billing stops.
   * Nothing is erased — the response carries the date the data is scheduled to
   * be removed, which is what the confirmation must show.
   *
   * `confirmName` must match the organization's name; the server checks it.
   */
  async deleteOrganization(tenantId: string, confirmName: string) {
    return this.request<{
      id: string;
      deletedAt: string;
      purgeAfter: string;
      graceDays: number;
      billing: string;
      sessionsRevoked: number | null;
      /** Drawn once, at delete time: after this response the organization answers nothing. */
      erasurePreview: TenantErasurePreview | null;
    }>(`/tenants/${tenantId}`, { method: 'DELETE', body: JSON.stringify({ confirmName }) });
  }

  /**
   * What deleting this organization would erase, counted while it is still
   * live. Drawn for the confirmation modal so the owner decides knowing the
   * scale. Owner-only, and best-effort: the caller shows "we could not count
   * this" rather than blocking the delete.
   */
  async getErasurePreview(tenantId: string) {
    return this.request<TenantErasurePreview>(`/tenants/${tenantId}/erasure-preview`);
  }

  // Get team member details by user_id (more robust for navigation)
  async getTeamMemberByUserId(userId: string) {
    return this.request<TeamMember>(`/team/members/user/${userId}`);
  }

  // Get all app access for a team member
  async getTeamMemberApps(memberId: string) {
    return this.request<{
      user: {
        user_id: string;
        email: string;
        name: string;
        first_name?: string;
        last_name?: string;
        avatar_url?: string;
        last_login?: string;
      };
      apps: Array<{
        id: string;
        user_id: string;
        app_id: string;
        app_name?: string;
        app_slug: string;
        description?: string;
        tenant_id: string;
        role: string;
        permissions: string[];
        status: string;
        joined_at: string;
        updated_at: string;
      }>;
    }>(`/team/members/${memberId}/apps`);
  }

  // Get all app access for a team member by user_id (more robust)
  async getTeamMemberAppsByUserId(userId: string) {
    return this.request<{
      user: {
        user_id: string;
        email: string;
        name: string;
        first_name?: string;
        last_name?: string;
        avatar_url?: string;
        last_login?: string;
      };
      apps: Array<{
        id: string;
        user_id: string;
        app_id: string;
        app_name?: string;
        app_slug: string;
        description?: string;
        tenant_id: string;
        role: string;
        permissions: string[];
        status: string;
        joined_at: string;
        updated_at: string;
      }>;
    }>(`/team/members/user/${userId}/apps`);
  }

  // Get all workspace memberships for a team member (read-only summary)
  async getTeamMemberWorkspaces(userId: string) {
    return this.request<{
      user: {
        user_id: string;
        email: string;
        name: string;
        first_name?: string;
        last_name?: string;
        avatar_url?: string;
        last_login?: string;
        status?: string;
      };
      workspaces: Array<{
        workspace_id: string;
        workspace_name: string;
        workspace_slug: string;
        workspace_role: 'admin' | 'member';
        joined_at: string;
        company: {
          id: string;
          name: string;
          slug: string;
        } | null;
        app_roles: Array<{
          app_id: string;
          app_name: string;
          app_slug: string;
          role: string;
        }>;
      }>;
    }>(`/team/members/user/${userId}/workspaces`);
  }

  // Get all apps for the current tenant
  async getTeamApps() {
    return this.request<Array<{
      id: string;
      name: string;
      slug: string;
      description?: string;
    }>>('/team/apps');
  }

  // Update team member role
  async updateTeamMemberRole(memberId: string, role: string) {
    return this.request<TeamMember>(`/team/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // Update team member status
  async updateTeamMemberStatus(memberId: string, status: string) {
    return this.request<TeamMember>(`/team/members/${memberId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Update team member status by user_id
  async updateTeamMemberStatusByUserId(userId: string, status: string) {
    return this.request<TeamMember>(`/team/members/user/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Update team member role by user_id
  async updateTeamMemberRoleByUserId(userId: string, role: string) {
    return this.request<TeamMember>(`/team/members/user/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // Remove team member
  async removeTeamMember(memberId: string) {
    return this.request<void>(`/team/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  // Invite new team member
  async inviteTeamMember(
    email: string, 
    role: string = 'user', 
    app_ids?: string[], 
    name?: string, 
    app_id?: string,
    app_roles?: Array<{ app_id: string; role: string }>
  ) {
    // Support both new app_ids array and legacy app_id
    const requestBody: any = { email, role, name };
    
    // Prefer app_roles if provided (per-app role assignment)
    if (app_roles && app_roles.length > 0) {
      requestBody.app_roles = app_roles;
    } else if (app_ids && app_ids.length > 0) {
      requestBody.app_ids = app_ids;
    } else if (app_id) {
      requestBody.app_id = app_id;
    }
    
    return this.request<any>('/team/invitations', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  /**
   * Re-send a pending invitation. The Auth service mints a fresh token, so the
   * previous link stops working and the expiry window restarts.
   */
  async resendTeamInvitation(invitationId: string) {
    return this.request<any>(`/team/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  }

  /**
   * Open requests to join this organization from people at its company domain
   * (docs/plans/one-organization-per-domain-plan.md, Step 3). Owners and admins only.
   */
  async getAccessRequests(): Promise<AccessRequest[]> {
    return this.request<AccessRequest[]>('/team/access-requests');
  }

  /** Approve: adds the requester to the chosen workspace with the chosen role. */
  async approveAccessRequest(requestId: string, body: { workspaceId: string; role: 'member' | 'admin' }) {
    return this.request<{ done: true }>(`/team/access-requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Decline: the requester is told by email and pointed at support. */
  async declineAccessRequest(requestId: string) {
    return this.request<{ done: true }>(`/team/access-requests/${encodeURIComponent(requestId)}/decline`, {
      method: 'POST',
    });
  }

  /** Cancel a pending invitation, revoking every pending invite that person holds. */
  async cancelTeamInvitation(invitationId: string) {
    return this.request<any>(`/team/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  // Invite new team member with workspace assignments
  async inviteTeamMemberWithWorkspaces(
    email: string,
    name?: string,
    workspace_roles?: Array<{ workspace_id: string; role: 'admin' | 'member' }>
  ) {
    const requestBody: any = { 
      email, 
      name,
      workspace_roles
    };
    
    return this.request<any>('/team/invitations', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  // Get team invitation status
  async getTeamInvitations() {
    return this.requestPaginated<any>('/team/invitations');
  }

  // Get user context (includes tenant and apps information)
  async getUserContext() {
    return this.request<{
      user: {
        id: string;
        email: string;
        name: string;
        role: string;
        status?: string;
        permissions?: string[];
      };
      tenant: {
        id: string;
        name: string;
        slug: string;
      };
      apps: Array<{
        id: string;
        name: string;
        slug: string;
        description?: string;
        role?: string;
        permissions?: string[];
      }>;
    }>('/team/context');
  }

  // Bulk operations for team members
  async bulkEnableApps(memberIds: string[], appIds: string[]) {
    return this.request<{ message: string }>('/team/members/bulk/enable-apps', {
      method: 'POST',
      body: JSON.stringify({
        member_ids: memberIds,
        app_ids: appIds
      })
    });
  }

  async bulkUpdateRole(memberIds: string[], role: string) {
    return this.request<{ message: string }>('/team/members/bulk/role', {
      method: 'POST',
      body: JSON.stringify({
        member_ids: memberIds,
        role
      })
    });
  }

  async bulkUpdateStatus(memberIds: string[], status: string) {
    return this.request<{ message: string }>('/team/members/bulk/status', {
      method: 'POST',
      body: JSON.stringify({
        member_ids: memberIds,
        status
      })
    });
  }

  // Bulk configure app access for team members
  async bulkConfigureApps(memberIds: string[], appConfigurations: Array<{
    app_id: string;
    role?: string;
    status?: string;
    revoke?: boolean;
  }>) {
    return this.request<{ message: string }>('/team/members/bulk/configure-apps', {
      method: 'POST',
      body: JSON.stringify({
        member_ids: memberIds,
        app_configurations: appConfigurations
      })
    });
  }

  // Get all member IDs matching the current filters (for cross-page selection)
  async getTeamMemberIds(params: {
    search?: string;
    role?: string;
    status?: string;
    app_id?: string;
  }) {
    const searchParams = new URLSearchParams();
    
    if (params.search) searchParams.append('search', params.search);
    if (params.role) searchParams.append('role', params.role);
    if (params.status) searchParams.append('status', params.status);
    if (params.app_id) searchParams.append('app_id', params.app_id);
    
    const url = searchParams.toString() 
      ? `/team/members/ids?${searchParams.toString()}`
      : '/team/members/ids';
      
    return this.request<string[]>(url);
  }

  async deleteFile(fileUrl: string): Promise<void> {
    return this.request<void>('/files/delete', {
      method: 'DELETE',
      body: JSON.stringify({ url: fileUrl }),
    });
  }

  async updateUserProfile(profileData: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    phone_number?: string;
    job_title?: string;
    department?: string;
    location?: string;
    bio?: string;
  }): Promise<any> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/me`, {
      method: 'PATCH',
      headers,
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      body: JSON.stringify(profileData)
    });

    if (!response.ok) {
      let errorData: any = null;
      try { errorData = await response.json(); } catch { /* ignore */ }
      // The Auth service answers { success: false, error: { code, message } } —
      // reading errorData.message found nothing, so every failure here showed
      // the bare status line instead of the reason.
      throw new Error(errorData?.error?.message || errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  // Session Management
  async getUserSessions(): Promise<Array<{
    id: string;
    is_current: boolean;
    is_active?: boolean;
    created_at: string;
    last_used: string;
    expires_at: string;
    ip_address: string | null;
    user_agent: string | null;
    device_info: Record<string, any>;
    browser: string;
    os: string;
    device_type: string;
  }>> {
    const headers = this.buildAuthServiceHeaders();

    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/session/list`, {
      method: 'GET',
      headers,
      credentials: 'include', // Include cookies for session verification
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    });

    if (!response.ok) {
      let errorData: any = null;
      try { errorData = await response.json(); } catch { /* ignore */ }
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  async revokeSession(sessionId: string): Promise<any> {
    const headers = this.buildAuthServiceHeaders();

    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/session/${sessionId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    });

    if (!response.ok) {
      let errorData: any = null;
      try { errorData = await response.json(); } catch { /* ignore */ }
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  async revokeAllOtherSessions(): Promise<{
    message: string;
    count: number;
  }> {
    const headers = this.buildAuthServiceHeaders();

    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/session/revoke-all-others`, {
      method: 'POST',
      headers,
      credentials: 'include',
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    });

    if (!response.ok) {
      let errorData: any = null;
      try { errorData = await response.json(); } catch { /* ignore */ }
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  // ========================================
  // Security Events / Activity Log API
  // ========================================

  async getSecurityEvents(params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
  }): Promise<{
    events: Array<{
      id: string;
      event_type: string;
      status: string;
      ip_address: string | null;
      user_agent: string | null;
      device_info: Record<string, any>;
      metadata: Record<string, any>;
      session_id: string | null;
      created_at: string;
    }>;
    pagination: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
    };
  }> {
    const headers = this.buildAuthServiceHeaders();

    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.event_type) queryParams.append('event_type', params.event_type);

    const url = `${getServiceApiUrl('auth')}/auth/security-events/list${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    const response = await authServiceFetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    });

    if (!response.ok) {
      let errorData: any = null;
      try { errorData = await response.json(); } catch { /* ignore */ }
      throw new Error(errorData?.error || `HTTP error! status: ${response.status}`);
    }

    // `json.data`, not the strip-`success` shape `requestPaginated`
    // uses. That shape is for the paginated envelope `{ success, data, meta }`,
    // where dropping `success` leaves `{ data, meta }`. This route answers
    // `{ success, data: { events, pagination } }` — one level deeper — so
    // stripping `success` returned `{ data: { … } }` and every caller read
    // `.events` as undefined. The Activity Log tab fell into its error state and
    // the "last changed" line on the Credentials tab, whose read is wrapped in a
    // catch, reported "never changed" for accounts that had just changed.
    const json = await response.json();
    return json.data;
  }

  // Recovery settings
  async getRecoverySettings(): Promise<{ backup_email: string | null; recovery_phone: string | null; backup_email_verified: boolean; recovery_phone_verified: boolean }> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/recovery/settings`, { method: 'GET', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to load recovery settings');
    const json = await response.json();
    return json.data;
  }

  async updateRecoverySettings(payload: { backup_email?: string; recovery_phone?: string }): Promise<any> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/recovery/settings`, { method: 'PATCH', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS), body: JSON.stringify(payload) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to update recovery settings');
    const json = await response.json();
    return json.data;
  }

  async generateBackupCodes(): Promise<{ codes: string[] }> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/recovery/backup-codes/generate`, { method: 'POST', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to generate backup codes');
    const json = await response.json();
    return json.data;
  }

  // ========================================
  // Two-factor authentication
  // ========================================
  /**
   * Call the auth service and unwrap its envelope, promoting a named failure to
   * an Error carrying `.code`.
   *
   * The MFA endpoints all answer with a `code` the UI branches on — INVALID_CODE,
   * TOO_MANY_ATTEMPTS, MFA_ALREADY_ENABLED — and branching on the message text
   * would break the first time the copy changed.
   */
  private async authServiceRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}${path}`, {
      headers: this.buildAuthServiceHeaders(),
      credentials: 'include',
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      ...init,
    });

    const json = await response.json().catch(() => null);

    if (!response.ok || !json?.success) {
      const error = new Error(json?.error?.message || 'Request failed') as Error & { code?: string };
      error.code = json?.error?.code;
      throw error;
    }

    return json.data as T;
  }

  /** Is two-factor authentication on for this account? */
  async getMfaStatus() {
    return this.authServiceRequest<{
      enabled: boolean;
      enrollmentPending: boolean;
      enabledAt: string | null;
      backupCodesRemaining: number;
    }>('/auth/mfa', { method: 'GET' });
  }

  /**
   * Begin enrolment. Returns the secret for manual entry and a QR rendered by
   * the auth service — the secret is never sent to a third party to be drawn.
   */
  async enrollMfa() {
    return this.authServiceRequest<{ secret: string; otpauthUrl: string; qrDataUrl: string }>(
      '/auth/mfa/totp/enroll',
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  /** Prove a code and turn the factor on. Returns the backup codes, once. */
  async activateMfa(code: string) {
    return this.authServiceRequest<{ enabled: boolean; backupCodes: string[] }>(
      '/auth/mfa/totp/activate',
      { method: 'POST', body: JSON.stringify({ code }) },
    );
  }

  /** Turn it off. Needs the password, not just a session. */
  async disableMfa(currentPassword: string) {
    return this.authServiceRequest<{ enabled: boolean }>('/auth/mfa/totp/disable', {
      method: 'POST',
      body: JSON.stringify({ currentPassword }),
    });
  }

  // ========================================
  // Password
  // ========================================
  /**
   * Change the signed-in user's own password.
   *
   * Rejects with an Error carrying `.code` — the auth service names the reason
   * (`INVALID_CURRENT_PASSWORD`, `PASSWORD_RECENTLY_USED`, `TOO_MANY_ATTEMPTS`,
   * …) and the caller maps that to a field. Branch on the code, never the
   * message: the message is the service's English text, not a contract.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ sessionsRevoked: number }> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/change-password`, {
      method: 'POST',
      headers,
      credentials: 'include',
      signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const json = await response.json().catch(() => null);

    if (!response.ok || !json?.success) {
      const error = new Error(json?.error?.message || 'Failed to change password') as Error & { code?: string };
      error.code = json?.error?.code || 'CHANGE_PASSWORD_FAILED';
      throw error;
    }

    return json.data;
  }

  // ========================================
  // Login Security Settings & Trusted Devices
  // ========================================
  async getLoginSecuritySettings(): Promise<{ email_on_new_login: boolean; require_mfa_for_sensitive: boolean; trust_device_days: number }> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/login-security/settings`, { method: 'GET', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to load login security settings');
    const json = await response.json();
    return json.data;
  }

  async updateLoginSecuritySettings(payload: Partial<{ email_on_new_login: boolean; require_mfa_for_sensitive: boolean; trust_device_days: number }>, confirmPassword?: string): Promise<{ email_on_new_login: boolean; require_mfa_for_sensitive: boolean; trust_device_days: number }> {
    const headers = this.buildAuthServiceHeaders();
    const reqBody = confirmPassword ? { ...payload, confirmPassword } : payload;
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/login-security/settings`, { method: 'PATCH', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS), body: JSON.stringify(reqBody) });
    if (!response.ok) {
      const errBody = await response.json();
      const err = new Error(errBody.error || 'Failed to update login security settings') as Error & { code?: string };
      err.code = errBody.code;
      throw err;
    }
    const json = await response.json();
    return json.data;
  }

  async getTrustedDevices(): Promise<Array<{ id: string; device_hash: string; user_agent: string | null; device_info: any; first_seen_at: string; last_seen_at: string; trusted_until: string | null }>> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/login-security/trusted-devices`, { method: 'GET', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to load trusted devices');
    const json = await response.json();
    return json.data;
  }

  async trustDevice(device_hash: string, trust_days?: number): Promise<any> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/login-security/trusted-devices`, { method: 'POST', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS), body: JSON.stringify({ device_hash, trust_days }) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to trust device');
    const json = await response.json();
    return json.data;
  }

  async deleteTrustedDevice(deviceHash: string): Promise<any> {
    const headers = this.buildAuthServiceHeaders();
    const response = await authServiceFetch(`${getServiceApiUrl('auth')}/auth/login-security/trusted-devices/${encodeURIComponent(deviceHash)}`, { method: 'DELETE', headers, credentials: 'include', signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
    if (!response.ok) throw new Error((await response.json()).error || 'Failed to delete trusted device');
    const json = await response.json();
    return json.data;
  }

  // ─── Plans & Subscriptions ──────────────────────────────────────────────────

  async getPlans() {
    return this.request<any[]>('/entitlements/plans');
  }

  async getMyPlan() {
    return this.request<any>('/entitlements/my-plan');
  }

  /** With a suite, also that suite's tier and trial (docs/plans/free-plan-and-reverse-trial-plan.md §5). */
  async getTenantStatus(suite?: string) {
    const query = suite ? `?suite=${encodeURIComponent(suite)}` : '';
    return this.request<any>(`/entitlements/tenant-status${query}`);
  }

  async createCheckoutSession(body: {
    planSlug: string;
    seats: number;
    organizationName: string;
    billingEmail: string;
  }) {
    return this.request<{ url: string }>('/billing/checkout-session', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async cancelSubscription(suite: string, timing: 'period_end' | 'immediate') {
    return this.request<{ timing: string; accessEndsAt: string | null }>('/billing/cancel', {
      method: 'POST',
      body: JSON.stringify({ suite, timing }),
    });
  }

  /** Move a live subscription to another tier of its suite, through Stripe. */
  async changePlan(planSlug: string) {
    return this.request<{ planSlug: string }>('/billing/change-plan', {
      method: 'POST',
      body: JSON.stringify({ planSlug }),
    });
  }

  /** The organization's invoices, read live from Stripe. */
  async getInvoices() {
    return this.request<import('./billing').InvoicesView>('/billing/invoices');
  }

  /** Billing email, address and saved payment methods, read live from Stripe. */
  async getBillingAccount() {
    return this.request<import('./billing').BillingAccount>('/billing/account');
  }

  /** A one-time URL into Stripe's billing portal. */
  async createPortalSession(body: { flow?: 'payment_method_update'; returnTab: string }) {
    return this.request<{ url: string }>('/billing/portal-session', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Set the seats bought on a suite's live subscription (ai-allowance-and-seat-caps-plan.md §4.5). */
  async changeSeats(suite: string, seats: number) {
    return this.request<{ seats: number; previousSeats: number | null }>('/billing/seats', {
      method: 'POST',
      body: JSON.stringify({ suite, seats }),
    });
  }

  /** The Start trial button for a suite. */
  async startSuiteTrial(suite: string) {
    return this.request<{ suite: string; expiresAt: string }>('/billing/start-trial', {
      method: 'POST',
      body: JSON.stringify({ suite }),
    });
  }

  // ─── Audit Log ─────────────────────────────────────────────────────────────

  async getAuditLogs(params: {
    category?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<{ data: AuditLogEntry[]; meta: { page: number; limit: number; total: number; totalPages: number } }> {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', String(params.page));
    if (params.pageSize) query.set('pageSize', String(params.pageSize));
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);
    const qs = query.toString();
    return this.requestPaginated<AuditLogEntry>(`/audit-log${qs ? `?${qs}` : ''}`);
  }

  async exportAuditLogs(params: {
    category?: string;
    search?: string;
  } = {}): Promise<Blob> {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    const url = `${this.baseUrl}/audit-log/export${qs ? `?${qs}` : ''}`;
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { headers, signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
    if (!response.ok) {
      throw new Error('Failed to export audit logs');
    }
    return response.blob();
  }
}

export const dashboardApiClient = new DashboardApiClient();
export default dashboardApiClient;
